/**
 * Button primitive — TASK 7.1.
 *
 * Extracted from the mock's repeated inline-styled `<button>`/`<a>` CTAs. The
 * `variant` map reproduces, one-for-one, the treatments that actually occur in
 * mock/SteamyMusic.dc.html — nothing was invented:
 *
 * | variant       | mock source                          | fill / label            |
 * |---------------|--------------------------------------|-------------------------|
 * | `primary`     | L290 ▶ PLAY, L393 PLAY ALL, L684     | #FF3E8E on #171221      |
 * | `accent`      | L69, L134, L145, L291 SHUFFLE, L394  | #FFD337 on #171221      |
 * | `retry`       | L80, L118, L159, L191, L362, L443    | #35E0C8 on #171221      |
 * | `google`      | L52 "Sign in with Google"            | #F7F2E8 on #171221      |
 * | `panel`       | L233/L340/L454/L497/L611 "Load more" | #241B33, teal label     |
 * | `panelText`   | L470 Rename                          | #241B33, cream label    |
 * | `panelDanger` | L471 Delete, L532, L591              | #241B33, pink label     |
 * | `ghost`       | L791/L806/L821 modal Cancel          | #171221, cream label    |
 * | `icon`        | L178/L268/L428 "⋯", L312 "♥", L722   | transparent, no border  |
 *
 * Every variant renders with a >= 44x44 px hit area: `.sm-btn` sets
 * `min-height`/`min-width: var(--sm-tap-min)` (44px) and `.sm-btn--icon`
 * pins an explicit 44x44 box, so even the glyph-only "⋯" menu button — 44x44
 * in the mock already — keeps a compliant target (TASK 7.1 Step 7 / W22).
 * Verified by apps/web/test/hit-area.test.ts against the parsed CSS rules.
 *
 * Handlers are wired with `addEventListener` only; there is no `on*=`
 * attribute and no `style=` string anywhere in this module (CSP W3).
 *
 * @module
 */

import { append, classes, el } from './dom.js';

/** Variant name -> extra class(es) on top of `.sm-btn`. */
const VARIANT_CLASS = {
  primary: ['sm-btn--primary'],
  accent: ['sm-btn--accent'],
  retry: ['sm-btn--retry'],
  google: ['sm-btn--google'],
  panel: ['sm-btn--panel'],
  panelText: ['sm-btn--panel-text'],
  panelDanger: ['sm-btn--panel-danger'],
  ghost: ['sm-btn--ghost'],
  icon: ['sm-btn--icon'],
};

/** Optional size modifier -> class. */
const SIZE_CLASS = {
  /** mock L290/L291 — 19px Bangers CTA, 48px tall. */
  display: 'sm-btn--display',
  /** mock L393-395/L443/L467/L468/L655/L684 — 17px Bangers CTA. */
  display17: 'sm-btn--display-17',
  /** mock L159/L191/L362 — compact inline Retry. */
  sm: 'sm-btn--sm',
  /** mock L521/L749 — 12px, 2px border, 40px tall. */
  xs: 'sm-btn--xs',
  /** mock L233/L340/L454/L497/L611 — full-width block. */
  block: 'sm-btn--block',
  /** mock L469-471 — 14px secondary. */
  secondary: 'sm-btn--secondary',
};

/**
 * @typedef {object} ButtonOptions
 * @property {string} label visible text (never HTML — set as textContent)
 * @property {keyof typeof VARIANT_CLASS} [variant='primary']
 * @property {keyof typeof SIZE_CLASS} [size] optional size modifier
 * @property {(event: Event) => void} [onClick] wired via addEventListener
 * @property {string} [testId] value for `data-testid`; sourced from the shared TESTIDS registry
 * @property {string} [ariaLabel] accessible name when the label is a glyph
 * @property {string} [href] render as an `<a>` (the mock uses link-shaped CTAs)
 * @property {'button'|'submit'} [type='button'] only when rendering a `<button>`
 * @property {boolean} [disabled=false]
 * @property {boolean} [pressed] renders `aria-pressed` for toggle buttons
 * @property {string} [icon] leading glyph rendered in its own span
 * @property {string|string[]} [className] extra class(es) (further mock modifiers)
 */

/**
 * Builds a button (or link-button) element.
 *
 * @param {ButtonOptions} options
 * @returns {HTMLElement}
 */
export function Button(options) {
  const {
    label,
    variant = 'primary',
    size,
    onClick,
    testId,
    ariaLabel,
    href,
    type = 'button',
    disabled = false,
    pressed,
    icon,
    className,
  } = options;

  const variantClasses = VARIANT_CLASS[variant];
  if (variantClasses === undefined) {
    throw new Error(`Button: unknown variant "${String(variant)}"`);
  }
  const sizeClass = size === undefined ? undefined : SIZE_CLASS[size];
  if (size !== undefined && sizeClass === undefined) {
    throw new Error(`Button: unknown size "${String(size)}"`);
  }

  const isLink = typeof href === 'string';
  /** @type {Record<string, string|number|boolean|null|undefined>} */
  const attrs = {};
  if (ariaLabel !== undefined) attrs['aria-label'] = ariaLabel;
  if (pressed !== undefined) attrs['aria-pressed'] = String(pressed);
  if (isLink) {
    attrs.href = href;
  } else {
    attrs.type = type;
    if (disabled) attrs.disabled = true;
  }

  const node = el(isLink ? 'a' : 'button', {
    className: classes(
      'sm-btn',
      ...variantClasses,
      sizeClass,
      ...(Array.isArray(className) ? className : classes(className)),
    ),
    testId,
    attrs,
    ...(onClick ? { on: { click: onClick } } : {}),
  });

  if (icon !== undefined) {
    append(
      node,
      el('span', { className: 'sm-btn__icon', text: icon, attrs: { 'aria-hidden': 'true' } }),
    );
  }
  // textContent, not innerHTML — labels are data, never markup.
  append(node, document.createTextNode(label));

  return node;
}

/**
 * The mock's cream "Sign in with Google" button (L52-55), including the
 * gradient-clipped "G" (L53). Kept as its own factory because the glyph is
 * structural, not a caller-supplied icon.
 *
 * @param {{ label?: string, onClick?: (event: Event) => void, testId?: string }} [options]
 * @returns {HTMLElement}
 */
export function GoogleButton(options = {}) {
  const { label = 'Sign in with Google', onClick, testId } = options;
  const node = el('button', {
    className: 'sm-btn sm-btn--google',
    testId,
    attrs: { type: 'button' },
    ...(onClick ? { on: { click: onClick } } : {}),
  });
  append(
    node,
    el('span', { className: 'sm-btn__google-g', text: 'G', attrs: { 'aria-hidden': 'true' } }),
    document.createTextNode(label),
  );
  return node;
}

/**
 * The mock's transparent glyph button — "⋯" (L178/L268/L428), "♥" (L312),
 * "✕" (L351/L606), the transport buttons (L722/L724). 44x44 by rule.
 *
 * @param {{
 *   glyph: string,
 *   ariaLabel: string,
 *   onClick?: (event: Event) => void,
 *   testId?: string,
 *   pressed?: boolean,
 *   tone?: 'default'|'muted'|'danger',
 *   className?: string|string[],
 * }} options
 * @returns {HTMLElement}
 */
export function IconButton(options) {
  const { glyph, ariaLabel, onClick, testId, pressed, tone = 'default', className } = options;
  const TONE_CLASS = {
    default: undefined,
    muted: 'sm-btn--icon-muted',
    danger: 'sm-btn--icon-danger',
  };
  if (!(tone in TONE_CLASS)) throw new Error(`IconButton: unknown tone "${String(tone)}"`);

  /** @type {Record<string, string|number|boolean|null|undefined>} */
  const attrs = { type: 'button', 'aria-label': ariaLabel };
  if (pressed !== undefined) attrs['aria-pressed'] = String(pressed);

  return el('button', {
    className: classes(
      'sm-btn',
      'sm-btn--icon',
      TONE_CLASS[tone],
      ...(Array.isArray(className) ? className : classes(className)),
    ),
    text: glyph,
    testId,
    attrs,
    ...(onClick ? { on: { click: onClick } } : {}),
  });
}
